import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  DataTable,
  DataTableCellContent,
  DataTableEmpty,
  DataTableEntitySelectorCell,
  EntitySelectorFooter,
  EntitySelectorFooterAction,
  DataTableFilterButton,
  DataTableGroupByDropdown,
  Badge,
  Icon,
  DataTablePopoverCell,
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  useDataTableState,
  createColumnHelper,
  renderDefaultGroupActions,
  resolveVisibleGroups,
  UNASSIGNED_GROUP_KEY,
  Box,
  Text,
  type DataTableColumnDef,
  type EntityItemConfig,
  type EntitySelectorConfig,
  Separator,
  type BulkAction,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
  formatShortDate,
  Skeleton,
} from "@salestrak/ui";

import {
  RoleSelector,
  UserSelector,
  renderTableEntityFilter,
} from "#app/components/entity-selectors";
import { ViewSearch } from "#app/components/filters/view-search";
import {
  teamToPoolItem,
  useTeamOpenBadge,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import { EntityTag, EntityGroupTag, EntitySelectionHover, type EntityData } from "#app/components/entity";
import {
  INVITED_STATUS_KEYS,
  MEMBER_STATUS_OPTIONS,
  MemberGroupChip,
  MemberQuickFilters,
  MemberQuickFiltersDivider,
  MemberSortChip,
  buildMemberQuickFilterDefs,
} from "#app/components/members/member-quick-filters";
import { DateText } from "#app/components/date-text";
import { MEMBER_STATUS_BADGE } from "#app/components/members/member-status-badge";
import {
  useMemberDirectory,
  useMemberEntityResolver,
} from "#app/lib/data/member-directory-provider";
import { useTableFilters, type FilterDef, type UseTableFiltersResult } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useMembersSiblingData } from "#app/lib/hooks/use-members-sibling-data";
import { MemberAccessCountButton } from "#app/components/members/member-access-popover";
import { AppMemberRowMenuItems, MemberRowMenuItems, MemberRowMenuDialog, type ConfirmAction } from "#app/components/members/member-row-menu";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { combineWhereConditions, buildOrderBy } from "#app/lib/query-utils";
import { TextValueCell } from "#app/components/fields/text-value-cell";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import {
  type MemberRow,
  MembersGroupDataDocument,
  MembersListDataDocument,
  type MembersListDataQuery,
  type RoleRow,
  type TeamOption,
  connectionRows,
} from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";
import { AnalyticsContext, MemberEvent, trackEvent } from "#app/lib/analytics";

// The bulk bar's count-chip noun: "1 Member selected".
const MEMBER_SELECTION_NOUN = { one: "Member", other: "Members" };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MembersTableVariant = "app" | "settings";

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const MEMBERS_PINNED_COLUMN_IDS = ["select", "name"];

// App variant: Date Joined ships hidden; the column picker can reveal it.
const MEMBERS_APP_DEFAULT_HIDDEN_COLUMN_IDS = ["creationDate"];

// Settings variant: Status / Joined / Reports to ship hidden — Status still
// reads on the default grouping's headers, Joined duplicates Invited On for
// every invite-created row, and the reporting line's primary home is the app
// members directory (reachable here from the column picker).
const MEMBERS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS = [
  "memberStatus",
  "creationDate",
  "manager",
];

type MembersTableProps = Omittable<{
  where: Record<string, unknown>;
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  variant?: MembersTableVariant;
  // App variant
  onViewProfile?: (userId: string) => void;
  /** Makes the Manager cell editable (app variant — the route passes it only
   *  when the viewer holds manage.users). */
  onManagerChange?: (userId: string, managerId: string | null) => void;
  // Settings variant
  onNavigateToProfile?: (userId: string) => void;
  availableTeams?: TeamOption[];
  availableRoles?: RoleRow[];
  onTeamsChange?: (userId: string, currentTeamIds: string[], newTeamIds: string[]) => void;
  /** Teams the viewer may manage (per-team `viewerCanManage` from the
   *  workspace team map) — scopes the Teams cell's picker and commit. */
  manageableTeamIds?: ReadonlySet<string>;
  /** Makes the Role cell editable (both variants — the app route passes it
   *  only when the viewer holds manage.users). */
  onRoleChange?: (userId: string, currentRoleIds: string[], roleId: string) => void;
  onFilterWhereChange?: (where: Record<string, unknown> | undefined) => void;
  actionButton?: ReactNode;
  // Per-group queries
  searchWhere?: Record<string, unknown>;
  /** Called when sibling data (roles, teams) is extracted from the ungrouped query. */
  onSiblingData?: (roles: RoleRow[], teams: TeamOption[]) => void;
  /** When provided, the table uses this filter state instead of creating its own. Pair with `hideToolbarActions` when filter/search UI lives in a parent header. */
  filterState?: UseTableFiltersResult<EntityItemConfig>;
  hideToolbarActions?: boolean;
  // Bulk actions (settings variant)
  bulkActions?: BulkAction<MemberRow>[];
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const h = createColumnHelper<MemberRow>();

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.users;

// ---------------------------------------------------------------------------
// Multi-select teams cell (settings variant)
// ---------------------------------------------------------------------------

function MemberTeamsCell({
  userId,
  currentTeams,
  availableTeams,
  manageableTeamIds,
  onCommit,
}: {
  userId: string;
  currentTeams: { teamId: string; teamName: string; teamKey?: string | null; icon?: { icon?: string | null; color?: string | null } | null }[];
  availableTeams: TeamOption[];
  /** Teams the VIEWER may manage (server-computed `viewerCanManage`). The
   *  picker offers only these, and the committed selection spans only these —
   *  a membership on any other team shows in the cell's stack but is never
   *  offered for removal, exactly mirroring the per-team server gate. */
  manageableTeamIds: ReadonlySet<string>;
  /** Receives the new selection over the MANAGEABLE set only. */
  onCommit: (userId: string, teamIds: string[]) => void;
}) {
  // The hover "Team" action on picker rows — the team counterpart of the
  // assignee picker's "Profile", navigating to the team page. The badge
  // carries its own handler and is undefined without the detail provider.
  const teamBadge = useTeamOpenBadge();

  const teamPool = useMemo(
    () => ({
      id: "teams",
      label: "Teams",
      items: availableTeams
        .filter((t) => manageableTeamIds.has(t.teamId))
        .map(teamToPoolItem),
      itemBadge: teamBadge,
    }),
    [availableTeams, manageableTeamIds, teamBadge],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [teamPool],
      searchPlaceholder: "Search teams...",
      emptyMessage: "No teams found",
    }),
    [teamPool],
  );

  // The picker's value is the manageable slice of the memberships; teams the
  // viewer can't manage never enter the selection, so a commit can't drop one.
  const teamIds = useMemo(
    () =>
      currentTeams
        .filter((t) => manageableTeamIds.has(t.teamId))
        .map((t) => t.teamId),
    [currentTeams, manageableTeamIds],
  );

  return (
    <DataTableEntitySelectorCell
      config={config}
      value={teamIds}
      onCommit={(ids) => onCommit(userId, ids)}
      contentProps={{ align: "start" }}
      selectedLabel="Teams"
      aria-label={currentTeams.length === 0 ? "Add teams" : undefined}
    >
      {currentTeams.length === 0 ? (
        <Icon name="user-plus" size="md" color="muted" />
      ) : (
        // One team or many, the cell is the tile stack — never tile + name.
        // Tiles hover to their team preview; the selector cell owns the click.
        <EntityGroupTag
          entities={currentTeams.map(
            (team): EntityData => ({ kind: "team", id: team.teamId, team }),
          )}
          variant="stack"
          size="xs"
          title="Teams"
          interactive={false}
        />
      )}
    </DataTableEntitySelectorCell>
  );
}

// ---------------------------------------------------------------------------
// Email cell — read-only, hover-revealed copy action
// ---------------------------------------------------------------------------

/**
 * The read-only Email column, through the shared email property cell: same
 * hover action row as a Contact's Email (mailto + copy), no editing. Shared by
 * every member roster (workspace members, team roster) so an email column
 * cannot ship without those affordances.
 */
export function MemberEmailCell({ email }: { email: string }) {
  return (
    <TextValueCell
      // A table cell hard-clips: the row-style end bleed would cut the
      // outline's corners square.
      bleedEnd={false}
      label="Email"
      onCommit={noopCommit}
      readOnly
      type="email"
      value={email}
    />
  );
}

/** The member's job title, read-only: the profile form and the profile
 *  header own the edit. An untitled member reads the shared empty label. */
export function MemberTitleCell({ title }: { title: string | null }) {
  return (
    <TextValueCell
      bleedEnd={false}
      label="Title"
      onCommit={noopCommit}
      readOnly
      type="text"
      value={title}
    />
  );
}

const noopCommit = () => {};

// ---------------------------------------------------------------------------
// Manager cell — the reporting line, edited in place
// ---------------------------------------------------------------------------

// The same property the profile panel's Manager row edits (who this member
// reports to), reading like an assignee cell: the avatar alone, the hover
// preview carrying the name. With a commit handler the cell opens the shared
// member picker single-select — only the member themself excluded; picking
// someone from their own reporting line lifts that person up (the server's
// rule, mirrored by useSetReportsTo); the footer's "No manager" clears the
// line. Without a handler the chip keeps its own press, so the cell wears
// the outline. Exported for the team roster's Manager column.
export function MemberManagerCell({
  userId,
  managerId,
  onCommit,
}: Omittable<{
  userId: string;
  managerId: string | null | undefined;
  onCommit?: (userId: string, managerId: string | null) => void;
}>) {
  // The member directory answers managers outside the bootstrap roster page.
  const resolveMember = useMemberEntityResolver();
  const { pendingOf } = useMemberDirectory();
  const managerEntity = managerId ? resolveMember(managerId) : null;
  // The cell trigger owns the press, so the chip is inert and the member
  // preview rides EntitySelectionHover — suppressed while the picker is up,
  // like every other selector cell's trigger chip.
  const [pickerOpen, setPickerOpen] = useState(false);
  const exclude = useMemo(() => [userId], [userId]);

  if (!onCommit) {
    if (!managerEntity) {
      return (
        <DataTableCellContent>
          {managerId && pendingOf(managerId) ? (
            <Skeleton circle delayed width={20} />
          ) : (
            <Icon name="user-plus" size="md" color="muted" />
          )}
        </DataTableCellContent>
      );
    }
    // Read-only: the avatar opens the manager's profile, edits nothing, and
    // so wears no ring.
    return (
      <DataTableCellContent>
        <EntityTag entity={managerEntity} display="visual" ghost={false} />
      </DataTableCellContent>
    );
  }

  return (
    <DataTablePopoverCell
      // The empty cell is a glyph alone; the name says what the press does.
      aria-label={managerEntity ? undefined : "Set reports to"}
      sheetTitle="Reports to"
      onOpenChange={setPickerOpen}
      contentProps={{ align: "start", padding: "none" }}
      content={(close) => (
        // Inline: the host popover owns open state, so the selector's close
        // intents (a single-select pick, the Profile action) route to close.
        <UserSelector
          variant="inline"
          mode="list"
          open
          title="Reports to"
          exclude={exclude}
          maxSelections={1}
          value={managerId}
          onValueChange={(ids) => onCommit(userId, ids[0] ?? null)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) close();
          }}
          footer={
            managerId ? (
              <EntitySelectorFooter>
                <EntitySelectorFooterAction
                  icon="x"
                  onClick={() => {
                    onCommit(userId, null);
                    close();
                  }}
                >
                  No manager
                </EntitySelectorFooterAction>
              </EntitySelectorFooter>
            ) : null
          }
        />
      )}
    >
      {managerEntity ? (
        <EntitySelectionHover entity={managerEntity} disabled={pickerOpen}>
          <EntityTag
            entity={managerEntity}
            display="visual"
            interactive={false}
          />
        </EntitySelectionHover>
      ) : (
        <Icon name="user-plus" size="md" color="muted" />
      )}
    </DataTablePopoverCell>
  );
}

// ---------------------------------------------------------------------------
// Read-only Teams cell
// ---------------------------------------------------------------------------

// The chips carry the interaction (hover preview, team page), so the cell
// wears the outline the editable variant gets from its selector trigger —
// one treatment, and the Teams column reads the same wherever it appears.
// An outline is a promise that something happens, so an empty cell has
// nothing to press and stays bare. Exported for the team roster.
export function MemberTeamsDisplayCell({ teams }: { teams: TeamOption[] }) {
  if (teams.length === 0) {
    return (
      <DataTableCellContent align="start">
        <Icon name="user-plus" size="md" color="muted" />
      </DataTableCellContent>
    );
  }
  return (
    <DataTableCellContent align="start">
      {/* One team or many, the cell is the tile stack — never tile + name.
        * Tiles hover to their preview and press to open the team; the cell
        * edits nothing, so it wears no ring. */}
      <EntityGroupTag
        entities={teams.map(
          (team): EntityData => ({ kind: "team", id: team.teamId, team }),
        )}
        size="xs"
        title="Teams"
      />
    </DataTableCellContent>
  );
}

// ---------------------------------------------------------------------------
// User-reference cell (Invited By)
// ---------------------------------------------------------------------------

// A cell never labels a member: the avatar alone, hovering to the member
// preview that carries the name. Read-only, so no ring.
function MemberUserCell({ userId }: { userId: string | null | undefined }) {
  const resolveMember = useMemberEntityResolver();
  const entity = userId ? resolveMember(userId) : null;
  if (!entity) {
    return (
      <DataTableCellContent>
        <Text size="sm" variant="muted">—</Text>
      </DataTableCellContent>
    );
  }
  return (
    <DataTableCellContent>
      <EntityTag entity={entity} display="visual" ghost={false} />
    </DataTableCellContent>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type UseMembersColumnsParams = Omittable<{
  variant: MembersTableVariant;
  // App variant
  onViewProfile?: (userId: string) => void;
  onManagerChange?: (userId: string, managerId: string | null) => void;
  // Settings variant
  onNavigateToProfile?: (userId: string) => void;
  onTeamsChange?: (userId: string, currentTeamIds: string[], newTeamIds: string[]) => void;
  /** Teams the viewer may manage (per-team `viewerCanManage`) — scopes the
   *  Teams cell's picker and its commit. Empty set = cell is read-only. */
  manageableTeamIds?: ReadonlySet<string>;
  // Both variants — presence makes the Role cell editable
  onRoleChange?: (userId: string, currentRoleIds: string[], roleId: string) => void;
  availableTeams: TeamOption[];
}>;

const NO_MANAGEABLE_TEAMS: ReadonlySet<string> = new Set();

// The role cell renders the member's OWN role; the pickable set is the role
// editor's, not a column's.
export function useMembersColumns({
  variant,
  onViewProfile,
  onManagerChange,
  onNavigateToProfile,
  onTeamsChange,
  manageableTeamIds = NO_MANAGEABLE_TEAMS,
  onRoleChange,
  availableTeams,
}: UseMembersColumnsParams): DataTableColumnDef<MemberRow>[] {
  return useMemo<DataTableColumnDef<MemberRow>[]>(
    () => {
      const handler = variant === "app" ? onViewProfile : onNavigateToProfile;

      const nameColumn = h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        meta:
          variant === "settings"
            ? { minWidth: 160, maxWidth: 280, width: 280 }
            : { width: 320 },
        content: (m) => (
          <EntityTag
            entity={{
              kind: "user",
              id: m.userId,
              user: {
                name: m.name,
                email: m.email,
                profileImage: m.profileImage,
                memberStatus: m.memberStatus,
              },
            }}
            interactive={false}
            preview
            presence
          />
        ),
        onClickAction: (m) => handler?.(m.userId),
        actionLabel: (m) => `View profile for ${m.name ?? m.email}`,
      });

      // "Status" everywhere — the same word the quick-filter chip, the funnel
      // def, and the group headers use for this facet.
      const statusColumn = h.badge({
        accessorKey: "memberStatus",
        header: "Status",
        enableSorting: true,
        badgeMap: MEMBER_STATUS_BADGE,
        fallback: "active",
      });

      const teamsColumn = h.display({
        id: "teams",
        header: "Teams",
        meta: { minWidth: 140 },
        cell: ({ row }) => {
          const teams = row.original.teams;

          // The cell is editable exactly as far as the server will say yes —
          // in BOTH variants: no handler, or no team the viewer can manage,
          // is a read-only display (an editable-looking cell whose save the
          // server rejects is worse than no affordance).
          if (!onTeamsChange || manageableTeamIds.size === 0) {
            return <MemberTeamsDisplayCell teams={teams} />;
          }

          return (
            <MemberTeamsCell
              userId={row.original.userId}
              currentTeams={teams}
              availableTeams={availableTeams}
              manageableTeamIds={manageableTeamIds}
              onCommit={(userId, newTeamIds) =>
                // Diff over the MANAGEABLE slice only — memberships on teams
                // the viewer can't manage never enter either side, so the
                // add/remove legs touch only teams the server permits.
                onTeamsChange(
                  userId,
                  teams
                    .map((t) => t.teamId)
                    .filter((id) => manageableTeamIds.has(id)),
                  newTeamIds,
                )
              }
            />
          );
        },
      });

      const rolesColumn = h.display({
        id: "roles",
        header: "Role",
        // Wide enough that the longest built-in role ("Limited Member")
        // reads whole rather than truncating at the default floor.
        meta: { minWidth: 140 },
        cell: ({ row }) => {
          const roles = row.original.roles;
          const roleText =
            roles.length === 0
              ? "None"
              : roles.map((r) => r.roleName).join(", ");

          const roleContent = (
            <Text size="sm" variant="muted" truncate={1}>
              {roleText}
            </Text>
          );

          // Editable wherever the host wires a role-change handler — the app
          // route passes one only when the viewer holds manage.users.
          if (!onRoleChange) {
            return (
              <DataTableCellContent>{roleContent}</DataTableCellContent>
            );
          }

          return (
            <DataTablePopoverCell
              // The RoleSelector's inline content already wears the standard
              // picker dressing — the popup adds none (issue-columns grammar).
              contentProps={{ padding: "none" }}
              sheetTitle="Role"
              content={(close) => (
                <RoleSelector
                  variant="inline"
                  value={roles[0]?.roleId ?? null}
                  onValueChange={(roleId) => { onRoleChange?.(row.original.userId, roles.map((r) => r.roleId), roleId); close(); }}
                />
              )}
            >
              {roleContent}
            </DataTablePopoverCell>
          );
        },
      });

      const emailColumn = h.accessor({
        accessorKey: "email",
        header: "Email",
        enableSorting: true,
        meta: { minWidth: 300 },
        cell: ({ getValue }) => (
          <MemberEmailCell email={(getValue() as string | null) ?? ""} />
        ),
      });

      // Job title, free text from the member's profile.
      const titleColumn = h.accessor({
        accessorKey: "title",
        header: "Title",
        enableSorting: true,
        meta: { minWidth: 200 },
        cell: ({ getValue }) => (
          <MemberTitleCell title={getValue() as string | null} />
        ),
      });

      const joinedColumn = h.date({
        accessorKey: "creationDate",
        header: variant === "app" ? "Date Joined" : "Joined",
      });

      // Reporting line (both variants; ships hidden on settings) — managerId
      // resolves through the workspace user map, so the column costs no
      // extra selection. Not
      // sortable: the server could only order by the raw id. Editable
      // wherever the host wires a manager-change handler, like the Role cell.
      // Labeled as the relationship ("Reports to"), never "Manager" — that
      // word is the team role's.
      const managerColumn = h.display({
        id: "manager",
        header: "Reports to",
        meta: { minWidth: 120, width: 180 },
        cell: ({ row }) => (
          <MemberManagerCell
            userId={row.original.userId}
            managerId={row.original.managerId}
            onCommit={onManagerChange}
          />
        ),
      });

      // Settings-only trio. Last Active sorts server-side (lastOnlineAt is
      // in core's orderBy allowlist); the invite columns don't sort — the
      // server resolves invitedById per row, not as an orderable column.
      const lastActiveColumn = h.accessor({
        accessorKey: "lastOnlineAt",
        header: "Last Active",
        enableSorting: true,
        meta: { minWidth: 120, textBrightness: "subtle" },
        cell: ({ row }) => (
          <DataTableCellContent>
            <DateText date={row.original.lastOnlineAt} />
          </DataTableCellContent>
        ),
      });

      const invitedByColumn = h.display({
        id: "invitedBy",
        header: "Invited By",
        meta: { minWidth: 100 },
        cell: ({ row }) => <MemberUserCell userId={row.original.invitedById} />,
      });

      // creationDate is the row's invite moment, but only an invite-created
      // row (invitedById present) may claim it — the workspace creator was
      // never invited and shows "—".
      const invitedOnColumn = h.display({
        id: "invitedOn",
        header: "Invited On",
        meta: { minWidth: 120, textBrightness: "subtle" },
        cell: ({ row }) => (
          <DataTableCellContent>
            <Text size={null} variant="muted" truncate>
              {formatShortDate(
                row.original.invitedById ? row.original.creationDate : null,
              )}
            </Text>
          </DataTableCellContent>
        ),
      });

      // Access column (settings only) — share-only role tiers
      // (hasPublicAccess=false) get the "N Workflows  N Items" cell;
      // each count is its own button opening the per-user popover for
      // that noun. Public-access roles see everything public already,
      // so the cell stays blank for them.
      const accessColumn = h.display({
        id: "access",
        header: "Access",
        meta: { minWidth: 160, width: 254 },
        cell: ({ row }) => {
          const m = row.original;
          if (m.hasPublicAccess) {
            return <DataTableCellContent>{null}</DataTableCellContent>;
          }
          // The two counts are the controls; the cell itself edits nothing,
          // so it carries no trigger (a trigger is a button, and a button
          // holding buttons is a broken tree).
          return (
            <DataTableCellContent>
              <Box direction="row" align="center" gap="sm">
                <MemberAccessCountButton
                  userId={m.userId}
                  kind="workflows"
                  count={m.sharedWorkflowCount}
                  label={
                    m.sharedWorkflowCount === 1 ? "Workflow" : "Workflows"
                  }
                />
                <MemberAccessCountButton
                  userId={m.userId}
                  kind="items"
                  count={m.sharedItemCount}
                  label={m.sharedItemCount === 1 ? "Item" : "Items"}
                />
              </Box>
            </DataTableCellContent>
          );
        },
      });

      // Definition order IS the default column order; default-hidden ids
      // (the MEMBERS_*_DEFAULT_HIDDEN_COLUMN_IDS lists) trail the visible set.
      if (variant === "app") {
        return [nameColumn, titleColumn, managerColumn, teamsColumn, emailColumn, rolesColumn, statusColumn, joinedColumn];
      }
      return [
        nameColumn,
        titleColumn,
        emailColumn,
        rolesColumn,
        accessColumn,
        teamsColumn,
        managerColumn,
        lastActiveColumn,
        invitedByColumn,
        invitedOnColumn,
        statusColumn,
        joinedColumn,
      ];
    },
    [
      variant,
      onViewProfile,
      onManagerChange,
      onNavigateToProfile,
      onTeamsChange,
      manageableTeamIds,
      onRoleChange,
      availableTeams,
    ]
  );
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

// Members open grouped by status; the Group chip rests there.
const MEMBERS_DEFAULT_GROUP_BY = "memberStatus";

// Status group buckets, in fixed display order (Active first) — the same
// buckets the Status chip offers.
const MEMBER_STATUS_GROUPS = MEMBER_STATUS_OPTIONS.map((o) => ({
  key: o.value,
  label: o.label,
}));

// The settings roster's funnel-panel filters. The app directory filters
// through the quick chips instead (`buildMemberQuickFilterDefs`).
//
// `FilterDef<EntityItemConfig>` — the pool item type travels through
// `useTableFilters` to `DataTableFilterButton`, which needs it to accept the
// app's `renderTableEntityFilter`. Left at the default `unknown`, the renderer
// does not typecheck and the entity rows silently render nothing.
export function buildMembersFilterDefs(
  teams: TeamOption[],
  roles: RoleRow[],
): FilterDef<EntityItemConfig>[] {
  const teamPoolItems = teams.map(teamToPoolItem);
  return [
    {
      key: "teamIds",
      label: "Team",
      icon: "users",
      type: "entity",
      pools: teams.length > 0 ? [{ id: "teams", label: "Teams", items: teamPoolItems }] : [],
      placeholder: "Search teams...",
      filterFormat: "idArray",
    },
    {
      key: "roleIds",
      label: "Role",
      icon: "shield",
      type: "select",
      options: roles.map((r) => ({ value: r.roleId, label: r.roleName })),
    },
    {
      key: "memberStatus",
      label: "Status",
      icon: "circle",
      type: "select",
      options: MEMBER_STATUS_OPTIONS,
      operator: "eq",
      multiple: false,
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MembersTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  onSearchChange,
  variant = "app",
  onViewProfile,
  onManagerChange,
  onNavigateToProfile,
  availableTeams = [],
  availableRoles = [],
  onTeamsChange,
  manageableTeamIds,
  onRoleChange,
  onFilterWhereChange,
  searchWhere,
  onSiblingData,
  bulkActions,
  actionButton,
  filterState: externalFilterState,
  hideToolbarActions,
}: MembersTableProps) {
  // --- Member row menu dialog state (lifted outside MenuContent) ---
  const [memberConfirmAction, setMemberConfirmAction] = useState<ConfirmAction | null>(null);
  // The departed member being re-invited; the invite dialog mounts fresh per
  // member (keyed) so its compose state starts from their address.
  const [reinviteMember, setReinviteMember] = useState<MemberRow | null>(null);

  // The app directory owns its filter state (the quick chips write it); the
  // settings roster passes its funnel-panel state in from the page header.
  const teamOpenBadge = useTeamOpenBadge();
  const quickFilterDefs = useMemo(
    () =>
      externalFilterState
        ? []
        : buildMemberQuickFilterDefs(availableTeams, availableRoles, teamOpenBadge),
    [availableTeams, availableRoles, externalFilterState, teamOpenBadge],
  );
  const internalFilterState = useTableFilters<EntityItemConfig>(quickFilterDefs);
  const tableFilters = externalFilterState ?? internalFilterState;

  useEffect(() => {
    onFilterWhereChange?.(tableFilters.where);
  }, [tableFilters.where, onFilterWhereChange]);

  const rawColumns = useMembersColumns({
    variant,
    onViewProfile,
    onManagerChange,
    onNavigateToProfile,
    onTeamsChange,
    manageableTeamIds,
    onRoleChange,
    availableTeams,
  });

  // Per-user column customization, keyed by variant — the app and settings
  // members tables show different column sets.
  const tableView = useTableViewSettings(
    `members:${variant}`,
    variant === "app"
      ? { defaultHiddenColumnIds: MEMBERS_APP_DEFAULT_HIDDEN_COLUMN_IDS }
      : { defaultHiddenColumnIds: MEMBERS_SETTINGS_DEFAULT_HIDDEN_COLUMN_IDS },
  );

  const {
    columns,
    groupByField,
    setGroupByField: setGroupByFieldRaw,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: rawColumns,
    defaultGroupByField: MEMBERS_DEFAULT_GROUP_BY,
    groupFields: {
      memberStatus: {
        label: "Status",
        icon: "circle",
        groups: MEMBER_STATUS_GROUPS,
        filterKey: "memberStatus",
        preserveOrder: true,
      },
      teams: {
        label: "Team",
        icon: "users",
        groups: [
          ...availableTeams.map((t) => ({ key: t.teamId, label: t.teamName })),
          { key: UNASSIGNED_GROUP_KEY, label: "No team" },
        ],
        filterKey: "teamIds",
      },
      roles: {
        label: "Role",
        icon: "shield",
        groups: [
          ...availableRoles.map((r) => ({ key: r.roleId, label: r.roleName })),
          { key: UNASSIGNED_GROUP_KEY, label: "No role" },
        ],
        filterKey: "roleIds",
      },
    },
  });
  const setGroupByField = useCallback(
    (field: string) => {
      trackEvent({
        context: AnalyticsContext.Member,
        event: MemberEvent.ChangeGrouping,
        data: { dimension: field, via: variant === "app" ? "members_table" : "settings_members" },
      });
      setGroupByFieldRaw(field);
    },
    [setGroupByFieldRaw, variant],
  );

  const groupWhereBuilders = useMemo<Record<string, (key: string) => Record<string, unknown>>>(() => ({
    memberStatus: (key) =>
      key === "invited"
        ? { memberStatus: { in: INVITED_STATUS_KEYS } }
        : { memberStatus: { eq: key } },
    teams: (key) =>
      key === UNASSIGNED_GROUP_KEY
        ? { teamIds: { isEmpty: true } }
        : { teamIds: { hasSome: [key] } },
    roles: (key) =>
      key === UNASSIGNED_GROUP_KEY
        ? { roleIds: { isEmpty: true } }
        : { roleIds: { hasSome: [key] } },
  }), []);

  // --- Group query state (used for both grouped and ungrouped) ---
  const { states, registerState, getGroupProps } = useGroupStates<MemberRow>();

  // Extract sibling data (roles, teams) from the ungrouped query result.
  const onSiblingDataRef = useRef(onSiblingData);
  onSiblingDataRef.current = onSiblingData;
  const handleUngroupedData = useCallback((rawQueryData: MembersListDataQuery | null) => {
    // Sibling connections ride along on the ungrouped query — the nodes ARE
    // the option types, so nothing is re-shaped on the way out.
    const roles = connectionRows(rawQueryData?.roles);
    const teams = connectionRows(rawQueryData?.teams);
    if (roles.length > 0 || teams.length > 0) {
      onSiblingDataRef.current?.(roles, teams);
    }
  }, []);

  // Grouped state never mounts the ungrouped provider the sibling connections
  // ride on, so it runs the same document with a minimal payload instead.
  const siblingData = useMembersSiblingData(!isGrouped);
  useEffect(() => {
    if (siblingData) handleUngroupedData(siblingData);
  }, [siblingData, handleUngroupedData]);

  // Determine visible groups when grouped.
  const visibleGroups = useMemo(() => {
    if (!isGrouped || !activeGroupConfig) return [];
    return resolveVisibleGroups(activeGroupConfig, tableFilters.filters, groupSortDirection);
  }, [isGrouped, activeGroupConfig, tableFilters.filters, groupSortDirection]);

  // Build the base where for per-group queries: search + non-group filters.
  const orderBy = useMemo(
    () => (orderByProp !== undefined ? orderByProp : sorting.length === 0 ? undefined : buildOrderBy(sorting, "name")),
    [orderByProp, sorting],
  );

  const groupBaseWhere = useMemo(() => {
    if (!isGrouped || !activeGroupConfig?.filterKey) {
      // No group filter to strip — combine search with all filters.
      const conditions: Record<string, unknown>[] = [];
      if (searchWhere) conditions.push(searchWhere);
      if (tableFilters.where) conditions.push(tableFilters.where);
      return combineWhereConditions(conditions);
    }
    // Strip the group-related filter from the filter where.
    const strippedFilterWhere = tableFilters.getWhereExcluding(activeGroupConfig.filterKey);
    const conditions: Record<string, unknown>[] = [];
    if (searchWhere) conditions.push(searchWhere);
    if (strippedFilterWhere) conditions.push(strippedFilterWhere);
    return combineWhereConditions(conditions);
  }, [isGrouped, activeGroupConfig, searchWhere, tableFilters]);

  // Status buckets hide when empty — an inert "Suspended 0" header is noise
  // on a roster that suspends nobody. A group stays visible until its total
  // is known, and when every bucket is empty (a search matching nobody) all
  // of them stay so the view doesn't render blank. The per-group query
  // providers still mount for hidden buckets, so one reappears the moment it
  // gains a member.
  const shownGroups = useMemo(() => {
    if (groupByField !== "memberStatus") return visibleGroups;
    const populated = visibleGroups.filter((g) => {
      const state = states.get(g.key);
      return !state || state.loading || state.total > 0;
    });
    return populated.length > 0 ? populated : visibleGroups;
  }, [groupByField, visibleGroups, states]);

  // Build DataTable groups.
  const groups = useMemo<DataTableGroupType<MemberRow>[]>(() => {
    if (!isGrouped) {
      return [{
        key: "__all__",
        label: "All Members",
        totalCount: states.get("__all__")?.total,
        ...getGroupProps("__all__"),
      }];
    }
    // Build groups from per-group query states.
    return shownGroups.map((g) => ({
      key: g.key,
      label: g.label,
      totalCount: states.get(g.key)?.total,
      ...getGroupProps(g.key),
    }));
  }, [isGrouped, shownGroups, states, getGroupProps]);

  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      const group = visibleGroups.find((g) => g.key === groupKey);
      if (groupKey === UNASSIGNED_GROUP_KEY) {
        // The unassigned label ("No team" / "No role") is declared on the
        // group config itself.
        return (
          <Text size="sm" variant="muted" weight="medium">
            {group?.label ?? "None"}
          </Text>
        );
      }
      if (groupByField === "memberStatus") {
        // The band wears the same chip the Status column and profile use,
        // so a bucket and its members' cells agree on what the status is.
        const status = MEMBER_STATUS_BADGE[groupKey];
        if (status) {
          return (
            <Badge color={status.color} tone={status.tone} size="chip">
              <Text size="xs" weight="medium">{status.label}</Text>
            </Badge>
          );
        }
      }
      if (groupByField === "teams") {
        const team = availableTeams.find((t) => t.teamId === groupKey);
        return (
          <EntityTag
            entity={{ kind: "team", id: groupKey, team: team ?? { teamName: groupKey } }}
            padded
          />
        );
      }
      return group?.label ?? groupKey;
    },
    [groupByField, availableTeams, visibleGroups]
  );

  return (
    <>
      {/* Ungrouped query provider */}
      {!isGrouped && (
        <GroupQueryProvider<MemberRow>
          groupKey="__all__"
          document={MembersListDataDocument}
          variables={{ where: whereProp, orderBy, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
          onData={handleUngroupedData}
        />
      )}

      {/* Per-group query providers (renderless) */}
      {isGrouped && groupWhereBuilders[groupByField] && visibleGroups.map((g) => {
        const groupWhere = combineWhereConditions(
          [groupBaseWhere, groupWhereBuilders[groupByField]?.(g.key)].filter(Boolean) as Record<string, unknown>[],
        );
        return (
          <GroupQueryProvider<MemberRow>
            key={g.key}
            groupKey={g.key}
            document={MembersGroupDataDocument}
            variables={{ where: groupWhere, orderBy, limit: 50 }}
            getConnection={getConnection}
              onStateChange={registerState}
          />
        );
      })}

      <DataTableToolbar>
        <DataTableToolbarLeft>
          {variant === "app" ? (
            // Group leads the band at the far left, the framing every toolbar
            // puts first; then the directory's quick filters, one labeled chip
            // per facet, then Sort past a hairline — all one chip species, so
            // the row reads as one set of controls.
            <>
              <DataTableGroupByDropdown
                groupByField={groupByField}
                setGroupByField={setGroupByField}
                groupByLabels={groupByLabels}
                groupByIcons={groupByIcons}
                groupSortDirection={groupSortDirection}
                setGroupSortDirection={setGroupSortDirection}
                // Baseline vs deviation, the date-window chip's rule: the
                // default grouping (Status) rests on the quiet outline with
                // its value on the face; any other choice — another field, or
                // ungrouped — lights the chip, and its × resets to the default
                // rather than to nothing.
                trigger={
                  <MemberGroupChip
                    value={isGrouped ? groupByLabels[groupByField] : "None"}
                    selected={groupByField !== MEMBERS_DEFAULT_GROUP_BY}
                    onClear={() => setGroupByField(MEMBERS_DEFAULT_GROUP_BY)}
                    clearLabel="Reset grouping"
                  />
                }
              />
              <MemberQuickFiltersDivider />
              <MemberQuickFilters
                filters={tableFilters}
                teams={availableTeams}
                roles={availableRoles}
                scopeWhere={searchWhere}
                via="members_table"
              />
              <MemberQuickFiltersDivider />
              <MemberSortChip
                sorting={sorting}
                onSortingChange={onSortingChange}
                via="members_table"
              />
            </>
          ) : (
            <DataTableGroupByDropdown
              groupByField={groupByField}
              setGroupByField={setGroupByField}
              groupByLabels={groupByLabels}
              groupByIcons={groupByIcons}
              groupSortDirection={groupSortDirection}
              setGroupSortDirection={setGroupSortDirection}
            />
          )}
        </DataTableToolbarLeft>
        {!hideToolbarActions && (
          <DataTableToolbarActions>
            {variant === "settings" && (
              <DataTableFilterButton
                filters={tableFilters.filters}
                activeFilterCount={tableFilters.activeFilterCount}
                clearAll={tableFilters.clearAll}
                setFilterValues={tableFilters.setFilterValues}
                renderEntityFilter={renderTableEntityFilter}
                tooltip="Quickly filter members"
              />
            )}
            <ViewSearch
              onValueChange={onSearchChange}
              size="xs"
            />
            {/* The trailing group past the separator: the view's own primary
                action, set apart from the filter cluster. Column show/hide
                lives on the actions column's circle-plus header instead. */}
            {actionButton && (
              <Box direction="row" align="center" gap="sm">
                <Separator orientation="vertical" className="h-4" />
                {actionButton}
              </Box>
            )}
          </DataTableToolbarActions>
        )}
      </DataTableToolbar>

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.userId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          gutterTop="md"
          density="regular"
          pinnedColumnIds={MEMBERS_PINNED_COLUMN_IDS}
          paddingX="lg"
          {...tableView.tableProps}
          // Enables the actions column's built-in circle-plus picker (the
          // two-section Show columns / Available popover).
          onColumnVisibilityChange={tableView.settingsProps.onColumnVisibilityChange}
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={renderDefaultGroupActions}
          // Two member tables on purpose. The app variant is a rolodex: find a
          // person, reach them, fix one row inline. Settings is the control
          // room where acting on many people at once belongs, so only it gets
          // the selection lane and the bulk bar.
          bulkActions={variant === "settings" ? bulkActions : undefined}
          bulkSelectionNoun={MEMBER_SELECTION_NOUN}
          renderRowActions={variant === "settings" ? (member) => (
            <MemberRowMenuItems
              member={member}
              availableRoles={availableRoles}
              onViewProfile={(userId) => onNavigateToProfile?.(userId)}
              onRoleChange={(userId, roleId) => onRoleChange?.(userId, member.roles.map((r) => r.roleId), roleId)}
              onRequestSuspend={(m) => setMemberConfirmAction({ type: "suspend", member: m })}
              onRequestReactivate={(m) => setMemberConfirmAction({ type: "reactivate", member: m })}
              onRequestCancelInvite={(m) => setMemberConfirmAction({ type: "cancel-invite", member: m })}
              onRequestReinvite={setReinviteMember}
            />
          ) : (member) => (
            <AppMemberRowMenuItems
              member={member}
              onRequestSuspend={(m) => setMemberConfirmAction({ type: "suspend", member: m })}
            />
          )}
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No members match your search"
                  : "No members found"
              }
            />
          }
        />
      </Box>

      <MemberRowMenuDialog
        action={memberConfirmAction}
        onClose={() => setMemberConfirmAction(null)}
      />
      {reinviteMember?.email ? (
        <InviteMembersDialog
          key={reinviteMember.userId}
          open
          initialEmails={[reinviteMember.email]}
          onOpenChange={(open) => {
            if (!open) setReinviteMember(null);
          }}
        />
      ) : null}
    </>
  );
}
